import type { Listing } from "@/types/domain";

const truthyFlags = new Set(["1", "yes", "true"]);
const falsyFlags = new Set(["0", "no", "false"]);
const pendingStatuses = new Set([
  "cancelled",
  "deleted",
  "inactive",
  "pending",
  "pending-approval",
  "pending approval",
  "pending_approval",
  "pendingapproval",
  "rejected",
]);

export type ListingManagementStatus =
  | "active"
  | "draft"
  | "pending"
  | "sold"
  | "expired";

function isTruthyFlag(value: unknown): boolean {
  return truthyFlags.has(String(value ?? "").trim().toLowerCase());
}

export function isListingSold(listing: Listing): boolean {
  const statuses = [listing.status, listing.managementStatus]
    .map((value) => String(value ?? "").trim().toLowerCase())
    .filter(Boolean);

  return (
    isTruthyFlag(listing.sold) ||
    isTruthyFlag(listing.soldOrWithdrawn) ||
    isTruthyFlag(listing.sold_or_withdrawn) ||
    statuses.includes("sold") ||
    statuses.includes("withdrawn")
  );
}

function approvalFlag(value: unknown): boolean | undefined {
  const normalized = String(value ?? "").trim().toLowerCase();
  if (truthyFlags.has(normalized)) return true;
  if (falsyFlags.has(normalized)) return false;
  return undefined;
}

function timestamp(value: unknown): number | undefined {
  if (value === undefined || value === null || value === "") return undefined;

  const numeric = Number(value);
  const parsed =
    Number.isFinite(numeric) && numeric > 0
      ? numeric < 10_000_000_000
        ? numeric * 1000
        : numeric
      : new Date(String(value)).getTime();
  return Number.isFinite(parsed) ? parsed : undefined;
}

export function isListingExpired(listing: Listing, now = Date.now()): boolean {
  const expiry = timestamp(listing.listingExpiry) ?? timestamp(listing.expiryTimestamp);
  return [listing.status, listing.managementStatus].some(
    (value) => String(value ?? "").trim().toLowerCase() === "expired",
  ) || (expiry !== undefined && expiry <= now);
}

/**
 * Resolve the status shown in My Listings.
 *
 * The API's legacy category buckets are only a fallback. In particular, an
 * approved Dairy listing can still arrive in the `pending` bucket, while its
 * own `approved` and `status` fields correctly say that it is active.
 */
export function getListingManagementStatus(
  listing: Listing,
  now = Date.now(),
): ListingManagementStatus {
  const status = String(listing.status ?? "").trim().toLowerCase();
  const bucketStatus = String(listing.managementStatus ?? "")
    .trim()
    .toLowerCase();
  const statuses = new Set([status, bucketStatus].filter(Boolean));

  if (isListingSold(listing)) return "sold";

  if (
    isTruthyFlag(listing.draftListing) ||
    isTruthyFlag(listing.draft_listing) ||
    statuses.has("draft")
  )
    return "draft";

  if (isListingExpired(listing, now))
    return "expired";

  const approved = approvalFlag(listing.approved);

  // The record's approval and status fields are more authoritative than the
  // response bucket, which can be stale or incorrectly cased.
  if (approved === true || status === "active") return "active";
  if (approved === false || pendingStatuses.has(status)) return "pending";

  if (bucketStatus === "active") return "active";
  return "pending";
}

export function isListingActive(
  listing: Listing,
  now = Date.now(),
): boolean {
  return getListingManagementStatus(listing, now) === "active";
}
